<template>
  <div v-if="shippingMethodsMetadata">
    <RevList class="-mt-8" :has-external-borders="false">
      <template #default>
        <div
          v-for="categoryGroup in shippingMethodsMetadata.shippingCategoryGroups"
          :key="categoryGroup.id"
          data-test="category-group"
        >
          <RevListItemStatic class="flex items-center py-48">
            <template #label>
              <span class="heading-1">{{ categoryGroup.name }}</span>
            </template>
            <template #description>
              <span class="text-static-default-low body-2 mt-8 inline-block">
                {{ categoryGroup.description }}
                <template v-if="categoryGroup.countMore">
                  {{ ' / ' }}
                  <RevLink
                    :underlined="false"
                    @click="handleOpenCategoryDetailsDrawer(categoryGroup)"
                  >
                    {{
                      i18n(translations.seeMore, {
                        count: categoryGroup.countMore,
                      })
                    }}
                  </RevLink>
                </template>
              </span>
            </template>
            <template #suffix>
              <RevButton
                v-if="
                  hasMissingCategories(
                    categoryGroup,
                    shippingRules?.[categoryGroup.id],
                  )
                "
                variant="primary"
                @click="
                  handleOpenRuleDrawer(
                    categoryGroup,
                    shippingRules?.[categoryGroup.id],
                  )
                "
              >
                {{ i18n(translations.addNewRule) }}
              </RevButton>
              <RevTooltip
                v-if="
                  !hasMissingCategories(
                    categoryGroup,
                    shippingRules?.[categoryGroup.id],
                  )
                "
                position="top"
              >
                <template #trigger="{ hide, show }">
                  <RevButton
                    disabled
                    variant="secondary"
                    @focusin="show"
                    @focusout="hide"
                    @mouseleave="hide"
                    @mouseover="show"
                  >
                    {{ i18n(translations.addNewRule) }}
                  </RevButton>
                </template>
                {{ i18n(translations.cannotAddNewRule) }}
              </RevTooltip>
            </template>
          </RevListItemStatic>
          <div
            v-if="shippingRules?.[categoryGroup.id]"
            class="mb-24 space-y-32"
          >
            <InfoBlocks
              :available-categories="categoryGroup.shippingCategories"
              :available-markets
              :category-group-rules="shippingRules?.[categoryGroup.id]"
            />
            <div
              v-for="rule in shippingRules[categoryGroup.id].rules"
              :key="rule.id"
              class="space-y-32"
              data-test="category-group-rule"
            >
              <div class="flex grow items-start justify-between">
                <div class="flex flex-wrap gap-16 pt-12">
                  <RevTag
                    v-for="category in formatCategories(categoryGroup, rule)"
                    :key="category.id"
                    :label="category.name"
                    variant="secondary"
                  />
                </div>
                <div
                  v-if="ruleIdToConfirmDeletion !== rule.id"
                  class="flex items-center space-x-8"
                >
                  <RevButtonIcon
                    :icon="IconEdit"
                    variant="secondary"
                    @click="
                      handleOpenRuleDrawer(
                        categoryGroup,
                        shippingRules[categoryGroup.id],
                        rule,
                      )
                    "
                  />
                  <RevButtonIcon
                    data-test="delete-btn"
                    :icon="IconTrash"
                    variant="secondary"
                    @click="handleConfirmRuleDeletion(rule)"
                  />
                </div>
                <div
                  v-if="ruleIdToConfirmDeletion === rule.id"
                  class="flex items-center space-x-12 py-4"
                >
                  <span>
                    {{ i18n(translations.deleteConfirm) }}
                  </span>
                  <RevButton
                    data-test="confirm-delete-btn"
                    size="small"
                    variant="primary"
                    @click="handleDeleteRule(rule)"
                  >
                    {{ i18n(translations.deleteOk) }}
                  </RevButton>
                  <RevButton
                    data-test="cancel-delete-btn"
                    size="small"
                    variant="secondary"
                    @click="handleCancelRuleDeletion"
                  >
                    {{ i18n(translations.deleteCancel) }}
                  </RevButton>
                </div>
              </div>
              <MethodList
                :category-group-metadata="{
                  id: categoryGroup.id,
                  preparationTime: rule.preparationTime,
                  shippingMethods: shippingMethodsMetadata,
                }"
                :is-free="false"
                :methods="[...rule.freeMethods, ...rule.paidMethods]"
              />
            </div>
          </div>
        </div>
      </template>
    </RevList>
    <RuleDrawer
      v-if="ruleDrawerData && sellerId"
      :available-markets
      :data="ruleDrawerData"
      :seller-id
      :shipping-methods-metadata
      @form-update="handleFormUpdate"
    />
    <CategoryGroupDetailsDrawer
      v-if="categoryGroupToDetail"
      :category-group="categoryGroupToDetail"
    />
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type MarketCountryCode, merchantsAPI } from '@backmarket/http-api'
import {
  type MarketWithSubMarkets,
  deleteShippingRule,
  getMarketsWithSubMarkets,
  getShippingRules,
} from '@backmarket/http-api/src/api-specs-shipping/rules'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLink } from '@ds/components/Link'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconTrash } from '@ds/icons/IconTrash'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { MODAL_NAMES } from '../../Shipping.constants'
import type { ShippingError } from '../../Shipping.types'
import { getApiErrorsMessage } from '../../Shipping.utils'

import translations from './CategoryList.translations'
import type {
  RuleDrawerData,
  ShippingCategoryGroupDetailed,
  ShippingCategoryGroupRules,
  ShippingRule,
} from './CategoryList.types'
import {
  transformShippingMethodsMetadata,
  transformShippingRules,
} from './CategoryList.utils'
import CategoryGroupDetailsDrawer from './components/CategoryGroupDetailsDrawer/CategoryGroupDetailsDrawer.vue'
import InfoBlocks from './components/InfoBlocks/InfoBlocks.vue'
import MethodList from './components/MethodList/MethodList.vue'
import RuleDrawer from './components/RuleDrawer/RuleDrawer.vue'

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const {
  publicId: sellerId,
  activeMarkets,
  potentialMarkets,
} = useSellerInfoStore()

const { data: marketsWithSubMarkets, error: marketsWithSubMarketsError } =
  await useHttpFetch(getMarketsWithSubMarkets, {
    pathParams: { sellerId },
  })

const [
  {
    data: shippingMethodsMetadata,
    error: shippingMethodsMetadataError,
    refresh: shippingMethodsMetadataRefresh,
  },
  {
    data: shippingRules,
    error: shippingRulesError,
    refresh: shippingRulesRefresh,
  },
] = await Promise.all([
  useHttpFetch(merchantsAPI.getShippingMethodsMetadata, {
    transform: transformShippingMethodsMetadata,
  }),
  useHttpFetch(getShippingRules, {
    pathParams: { sellerId },
    transform: ({ shippingCategoryGroups }) =>
      transformShippingRules(
        shippingCategoryGroups,
        marketsWithSubMarkets.value || [],
      ),
  }),
])

const error = computed(
  () =>
    shippingMethodsMetadataError.value ||
    shippingRulesError.value ||
    marketsWithSubMarketsError.value,
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const ruleDrawerData = ref<RuleDrawerData>()
const categoryGroupToDetail = ref<ShippingCategoryGroupDetailed>()
const apiError = ref<ShippingError['errors']>()
const isSubmitting = ref(false)
const ruleIdToConfirmDeletion = ref()

const availableMarkets = computed(() => {
  const filteredMarkets: MarketWithSubMarkets[] = []

  Object.values([...activeMarkets, ...potentialMarkets])
    .sort()
    .forEach((marketToDisplay) => {
      const marketWithSubMarkets = marketsWithSubMarkets.value?.find(
        ({ market }) => market === marketToDisplay,
      )

      filteredMarkets.push({
        market: marketToDisplay as MarketCountryCode,
        subMarkets: marketWithSubMarkets?.subMarkets || [],
      })
    })

  return filteredMarkets
})

function refresh() {
  shippingMethodsMetadataRefresh()
  shippingRulesRefresh()
}

function formatCategories(
  categoryGroup: ShippingCategoryGroupDetailed,
  rule: ShippingRule,
) {
  return categoryGroup.shippingCategories.filter(({ id }) =>
    rule.categories.includes(id),
  )
}

function hasMissingCategories(
  categoryGroup: ShippingCategoryGroupDetailed,
  categoryGroupRules?: ShippingCategoryGroupRules,
) {
  return (
    !categoryGroupRules ||
    categoryGroup.shippingCategories.length >
      categoryGroupRules.categories.length
  )
}

function handleOpenCategoryDetailsDrawer(
  categoryGroup: ShippingCategoryGroupDetailed,
) {
  categoryGroupToDetail.value = categoryGroup
  openModal(MODAL_NAMES.CATEGORY_DETAILS)
}

function handleOpenRuleDrawer(
  categoryGroup: ShippingCategoryGroupDetailed,
  categoryGroupRules?: ShippingCategoryGroupRules,
  rule?: ShippingRule,
) {
  ruleDrawerData.value = {
    categoryGroup,
    markets: categoryGroupRules?.markets || [],
    categories: categoryGroupRules?.categories || [],
    rule,
  }
  openModal(MODAL_NAMES.SHIPPING_RULES)
}

function handleFormUpdate() {
  refresh()
}

function handleConfirmRuleDeletion(rule: ShippingRule) {
  ruleIdToConfirmDeletion.value = rule.id
}

function handleCancelRuleDeletion() {
  ruleIdToConfirmDeletion.value = undefined
}

async function handleDeleteRule(rule: ShippingRule) {
  apiError.value = []
  isSubmitting.value = true
  ruleIdToConfirmDeletion.value = undefined
  try {
    await $httpFetch(deleteShippingRule, {
      pathParams: {
        sellerId,
        shippingRuleId: rule.id,
      },
    })
    refresh()
  } catch (err) {
    apiError.value = getApiErrorsMessage(err as ShippingError)
  } finally {
    isSubmitting.value = false
  }
}
</script>
